import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  FormControlLabel,
  InputAdornment,
  LinearProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import DownloadRounded from '@mui/icons-material/DownloadRounded'
import { adminService, type ConciergeUsageReport } from '../../services/adminService'
import { LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { CONCIERGE_NAME } from '../../config/concierge'

const usd = (n: number) => `$${n.toFixed(2)}`
const num = (n: number) => n.toLocaleString()

export default function ConciergeManager({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  const [report, setReport] = useState<ConciergeUsageReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const load = () =>
    adminService
      .getConciergeUsage(eventId)
      .then(setReport)
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  const toggle = async (enabled: boolean) => {
    setSaving(true)
    try {
      const res = await adminService.setConciergeConfig(eventId, { enabled })
      setReport((r) => (r ? { ...r, eventEnabled: res.enabled } : r))
      notify(
        enabled
          ? `${CONCIERGE_NAME} enabled for this event`
          : `${CONCIERGE_NAME} disabled`,
      )
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingState />
  if (!report) return null

  const t = report.totals
  const today = report.today

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6" fontWeight={700}>
          {CONCIERGE_NAME} — AI Concierge
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {CONCIERGE_NAME} is an attendee chat assistant grounded in this
          event&apos;s data, powered by Microsoft Foundry. Usage is billed per token
          — watch the daily budget.
        </Typography>
      </Box>

      {!report.globalEnabled && (
        <Alert severity="info">
          The AI Concierge is turned off platform-wide (the global{' '}
          <code>AI_CONCIERGE_ENABLED</code> flag is off). Enabling {CONCIERGE_NAME}{' '}
          for this event has no effect until an administrator turns it on globally.
        </Alert>
      )}

      <Card variant="outlined" sx={{ p: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={report.eventEnabled}
              disabled={saving}
              onChange={(e) => toggle(e.target.checked)}
            />
          }
          label={
            <Box>
              <Typography fontWeight={600}>
                Enable {CONCIERGE_NAME} for this event
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Attendees see the &quot;Ask {CONCIERGE_NAME}&quot; button only when
                this is on (and the global flag is on).
              </Typography>
            </Box>
          }
        />
      </Card>

      {/* Per-event total budget cap (auto-disables when reached) */}
      <BudgetCard
        eventId={eventId}
        budget={report.budget}
        onSaved={load}
      />

      {/* Today vs. budget */}
      <Card variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
        >
          <Box>
            <Typography variant="overline" color="text.secondary">
              Today ({today.date})
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {usd(today.estCostUsd)}
              {today.dailyBudgetUsd > 0 && (
                <Typography component="span" variant="body1" color="text.secondary">
                  {' '}
                  / {usd(today.dailyBudgetUsd)}
                </Typography>
              )}
            </Typography>
          </Box>
          {today.capReached ? (
            <Chip color="warning" label="Daily budget reached — paused for today" />
          ) : (
            <Chip color="success" variant="outlined" label="Within budget" />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Cost is an estimate (list price × tokens), not an invoice.
        </Typography>
      </Card>

      {/* All-time totals */}
      <Card variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          All-time totals
        </Typography>
        <Stack direction="row" spacing={4} flexWrap="wrap">
          <Metric label="Requests" value={num(t.requests)} />
          <Metric label="Prompt tokens" value={num(t.promptTokens)} />
          <Metric label="Completion tokens" value={num(t.completionTokens)} />
          <Metric label="Total tokens" value={num(t.totalTokens)} />
          <Metric label="Est. cost" value={usd(t.estCostUsd)} />
        </Stack>
      </Card>

      {/* Per-model split */}
      {report.perModel.length > 1 && (
        <Card variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            By model
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Model</TableCell>
                <TableCell align="right">Requests</TableCell>
                <TableCell align="right">Total tokens</TableCell>
                <TableCell align="right">Est. cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.perModel.map((m) => (
                <TableRow key={m.model}>
                  <TableCell>{m.model || '—'}</TableCell>
                  <TableCell align="right">{num(m.requests)}</TableCell>
                  <TableCell align="right">
                    {num(m.promptTokens + m.completionTokens)}
                  </TableCell>
                  <TableCell align="right">{usd(m.estCostUsd)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Per-day trend + export */}
      <Card variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="subtitle2">Daily usage</Typography>
          <Button
            size="small"
            startIcon={<DownloadRounded />}
            component="a"
            href={adminService.conciergeUsageCsvUrl(eventId)}
          >
            Export CSV
          </Button>
        </Stack>
        <Divider sx={{ mb: 1 }} />
        {report.perDay.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No usage yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell align="right">Requests</TableCell>
                <TableCell align="right">Total tokens</TableCell>
                <TableCell align="right">Est. cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.perDay.map((d) => (
                <TableRow key={d.date}>
                  <TableCell>{d.date}</TableCell>
                  <TableCell align="right">{num(d.requests)}</TableCell>
                  <TableCell align="right">
                    {num(d.promptTokens + d.completionTokens)}
                  </TableCell>
                  <TableCell align="right">{usd(d.estCostUsd)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </Stack>
  )
}

function BudgetCard({
  eventId,
  budget,
  onSaved,
}: {
  eventId: string
  budget: ConciergeUsageReport['budget']
  onSaved: () => void
}) {
  const { notify } = useToast()
  const [value, setValue] = useState<string>(
    budget.budgetUsd != null ? String(budget.budgetUsd) : '',
  )
  const [saving, setSaving] = useState(false)

  const save = async () => {
    setSaving(true)
    try {
      const budgetUsd = value.trim() === '' ? null : Number(value)
      if (budgetUsd != null && (Number.isNaN(budgetUsd) || budgetUsd < 0)) {
        notify('Enter a valid budget amount (or leave blank for no cap)', 'error')
        return
      }
      await adminService.setConciergeConfig(eventId, { budgetUsd })
      notify(budgetUsd ? 'Budget saved' : 'Budget cap removed')
      onSaved()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  const cap = budget.budgetUsd ?? 0
  const spent = budget.totalSpendUsd
  const pct = cap > 0 ? Math.min(100, (spent / cap) * 100) : 0

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Total budget cap
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Set a total spend limit for {CONCIERGE_NAME} at this event. When cumulative
        spend reaches it, {CONCIERGE_NAME} is <strong>automatically turned off</strong>{' '}
        for the event. Leave blank for no cap.
      </Typography>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: cap > 0 ? 1.5 : 0 }}>
        <TextField
          label="Budget"
          size="small"
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="No cap"
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          sx={{ width: 180 }}
        />
        <Button variant="contained" size="small" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save budget'}
        </Button>
      </Stack>

      {cap > 0 && (
        <Box>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {usd(spent)} of {usd(cap)} spent
            </Typography>
            {budget.capReached && (
              <Chip
                size="small"
                color="error"
                label={`Budget reached — ${CONCIERGE_NAME} auto-disabled`}
              />
            )}
          </Stack>
          <LinearProgress
            variant="determinate"
            value={pct}
            color={budget.capReached ? 'error' : pct > 80 ? 'warning' : 'primary'}
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Box>
      )}
    </Card>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700}>
        {value}
      </Typography>
    </Box>
  )
}
